import { AsyncSearchableSelect, type SearchableOption } from "@/components/SearchableSelect";
import { onboarding, type OnboardingAgent } from "@/features/onboarding/api/onboarding.api";

export const AGENT_PAGE_SIZE = 20;
const AGENT_LOOKUP_PAGE_SIZE = 100;

function agentLabel(a: OnboardingAgent) {
  return `#${a.userNumber} — ${a.name}`;
}

export async function findAgentByNumber(agentNumber: string): Promise<OnboardingAgent | null> {
  const page = await onboarding.agents({ keyword: agentNumber, pageSize: AGENT_LOOKUP_PAGE_SIZE });
  return page.items.find((a) => String(a.userNumber) === agentNumber) ?? null;
}

async function searchByNumber({ keyword, pageNumber }: { keyword: string; pageNumber: number }) {
  const page = await onboarding.agents({ keyword, pageNumber, pageSize: AGENT_PAGE_SIZE });
  return {
    options: page.items.map((a) => ({ value: String(a.userNumber), label: agentLabel(a), keywords: a.name })),
    hasNextPage: page.hasNextPage,
    totalCount: page.totalCount,
  };
}

async function resolveByNumber(agentNumber: string): Promise<SearchableOption> {
  const a = await findAgentByNumber(agentNumber).catch(() => null);
  return a ? { value: agentNumber, label: agentLabel(a), keywords: a.name } : { value: agentNumber, label: `#${agentNumber}` };
}

async function searchById({ keyword, pageNumber }: { keyword: string; pageNumber: number }) {
  const page = await onboarding.agents({ keyword, pageNumber, pageSize: AGENT_PAGE_SIZE });
  return {
    options: page.items.map((a) => ({ value: a.id, label: agentLabel(a), keywords: a.name })),
    hasNextPage: page.hasNextPage,
    totalCount: page.totalCount,
  };
}

export function AgentNumberSelect({
  value, onValueChange, id, className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  id?: string;
  className?: string;
}) {
  return (
    <AsyncSearchableSelect
      id={id}
      value={value}
      onValueChange={onValueChange}
      search={searchByNumber}
      resolveValue={resolveByNumber}
      placeholder="Select a user"
      searchPlaceholder="Search by name or user number"
      emptyText="No users match that search."
      className={className}
    />
  );
}

export function AgentIdSelect({
  value, onValueChange, pinnedOptions, icon, className, contentClassName,
}: {
  value: string;
  onValueChange: (value: string) => void;
  pinnedOptions?: SearchableOption[];
  icon?: React.ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <AsyncSearchableSelect
      value={value}
      onValueChange={onValueChange}
      search={searchById}
      pinnedOptions={pinnedOptions}
      searchPlaceholder="Search agents"
      emptyText="No agents match that search."
      icon={icon}
      className={className}
      contentClassName={contentClassName}
    />
  );
}
